import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import { paymentsApi } from '../api'
import useFetch from '../hooks/useFetch'
import {
  Page, PageHead, Card, StatGrid, Stat, TableWrapper, Table, StatusPill,
  Loading, ErrorState, EmptyState, Input, Select, Button,
} from '../Components/ui'
import { colors, bp } from '../theme'

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;

  input { flex: 1; min-width: 220px; }

  @media (max-width: ${bp.mobile}) {
    input { min-width: unset; width: 100%; }
  }
`

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1.25rem;
  gap: 1rem;
  flex-wrap: wrap;

  .info { color: ${colors.slate}; font-size: 0.88rem; }
  .btns { display: flex; gap: 0.5rem; }
`

const money = (n) => `KES ${Number(n || 0).toLocaleString()}`
const litres = (ml) => `${(Number(ml || 0) / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} L`
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—')

const PAGE_SIZE = 10

const Payments = () => {
  const { data: payments, loading, error, reload } = useFetch(() => paymentsApi.list(), [])
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState('all')
  const [page, setPage] = useState(1)

  const list = useMemo(() => payments || [], [payments])

  const summary = useMemo(() => {
    const done = list.filter((p) => p.statusState === 'success')
    return {
      revenue: done.reduce((s, p) => s + p.amount, 0),
      litres: done.reduce((s, p) => s + p.units, 0),
      count: done.length,
    }
  }, [list])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return list.filter((p) => {
      const matchQ = !q || p.phone.toLowerCase().includes(q) || p.mpesaCode.toLowerCase().includes(q)
      const matchS = status === 'all' || p.statusState === status
      return matchQ && matchS
    })
  }, [list, query, status])

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const current = Math.min(page, pageCount)
  const rows = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE)

  // Reset to page 1 when filters change.
  const onFilter = (fn) => (v) => { fn(v); setPage(1) }

  return (
    <Page>
      <PageHead>
        <h1>Payments</h1>
        <p>M-Pesa transactions, revenue, and detergent volume dispensed.</p>
      </PageHead>

      {loading && <Card><Loading label="Loading payments…" /></Card>}
      {error && <Card><ErrorState message={error.message} onRetry={reload} /></Card>}

      {!loading && !error && (
        <>
          <StatGrid cols={3}>
            <Stat label="Completed Revenue" value={money(summary.revenue)} sub="all time" />
            <Stat label="Detergent Dispensed" value={litres(summary.litres)} sub="completed payments" />
            <Stat label="Completed Transactions" value={summary.count} sub={`${list.length} total`} />
          </StatGrid>

          <Card>
            <Toolbar>
              <Input
                placeholder="Search by phone or receipt…"
                value={query}
                onChange={(e) => onFilter(setQuery)(e.target.value)}
              />
              <Select value={status} onChange={(e) => onFilter(setStatus)(e.target.value)}>
                <option value="all">All statuses</option>
                <option value="success">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </Select>
            </Toolbar>

            {filtered.length === 0 ? (
              <EmptyState
                title={list.length === 0 ? 'No payments yet' : 'No matches'}
                message={list.length === 0
                  ? 'Transactions appear here once the dispenser processes M-Pesa payments.'
                  : 'Try a different search or filter.'}
              />
            ) : (
              <>
                <TableWrapper>
                  <Table min="820px">
                    <thead>
                      <tr>
                        <th>Receipt</th>
                        <th>Phone</th>
                        <th>Method</th>
                        <th>Amount</th>
                        <th>Volume</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((p) => (
                        <tr key={p.id}>
                          <td>{p.mpesaCode}</td>
                          <td>{p.phone}</td>
                          <td>{p.method}</td>
                          <td>{money(p.amount)}</td>
                          <td>{litres(p.units)}</td>
                          <td><StatusPill state={p.statusState} label={p.statusLabel} /></td>
                          <td>{fmtDate(p.date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrapper>

                <Pager>
                  <span className="info">
                    Showing {rows.length} of {filtered.length} — page {current} / {pageCount}
                  </span>
                  <div className="btns">
                    <Button small variant="ghost" disabled={current <= 1}
                      onClick={() => setPage(current - 1)}>Previous</Button>
                    <Button small variant="ghost" disabled={current >= pageCount}
                      onClick={() => setPage(current + 1)}>Next</Button>
                  </div>
                </Pager>
              </>
            )}
          </Card>
        </>
      )}
    </Page>
  )
}

export default Payments
